/**
 * NEXUS UI - LineChart
 * Multi-series line chart with hover interactions
 */

import React, { useState, useMemo } from 'react';
import type { BaseProps, NexusVariant, DataPoint } from './types';

interface LineSeries {
  id: string;
  label: string;
  data: DataPoint[];
  color?: NexusVariant;
  dashed?: boolean;
}

export interface LineChartProps extends BaseProps {
  series: LineSeries[];
  width?: number;
  height?: number;
  showGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showLegend?: boolean;
  showDots?: boolean;
  showTooltip?: boolean;
  animated?: boolean;
  curved?: boolean;
  yAxisFormat?: (value: number) => string;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const defaultColors: NexusVariant[] = ['cyan', 'blue', 'violet', 'green', 'amber', 'red'];

export const LineChart: React.FC<LineChartProps> = ({
  series,
  width = 500,
  height = 300,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showLegend = true,
  showDots = true,
  showTooltip = true,
  animated = true,
  curved = true,
  yAxisFormat,
  className = '',
  style,
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<{ seriesIndex: number; pointIndex: number } | null>(null);
  const [activeSeries, setActiveSeries] = useState<Set<string>>(new Set(series.map(s => s.id)));

  const padding = useMemo(() => ({ top: 20, right: 20, bottom: 40, left: 50 }), []);
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const { processedSeries, yMin, yMax, yTicks, xLabels } = useMemo(() => {
    const visibleSeries = series.filter(s => activeSeries.has(s.id));

    if (visibleSeries.length === 0) {
      return { processedSeries: [], yMin: 0, yMax: 100, yTicks: [], xLabels: [] };
    }

    // Find global min/max
    let minVal = Infinity;
    let maxVal = -Infinity;
    let maxLength = 0;

    visibleSeries.forEach(s => {
      s.data.forEach(d => {
        minVal = Math.min(minVal, d.value);
        maxVal = Math.max(maxVal, d.value);
      });
      maxLength = Math.max(maxLength, s.data.length);
    });

    // Add padding to range
    const range = maxVal - minVal || 1;
    const paddedMin = minVal - range * 0.1;
    const paddedMax = maxVal + range * 0.1;
    const paddedRange = paddedMax - paddedMin;

    // Calculate y-axis ticks
    const tickCount = 5;
    const yTickValues = Array.from({ length: tickCount }, (_, i) =>
      paddedMin + (paddedRange * i) / (tickCount - 1)
    );

    // Get x-axis labels from first series
    const xAxisLabels = visibleSeries[0]?.data.map(d => d.label || '') || [];

    // Process each series into points and path
    const processed = series.map((s, seriesIndex) => {
      const color = s.color || defaultColors[seriesIndex % defaultColors.length];
      const colors = variantColors[color];
      const isVisible = activeSeries.has(s.id);

      const points = s.data.map((d, pointIndex) => {
        const x = padding.left + (pointIndex / (maxLength - 1)) * chartWidth;
        const y = padding.top + chartHeight - ((d.value - paddedMin) / paddedRange) * chartHeight;
        return { x, y, data: d };
      });

      // Generate path
      let path = '';
      if (curved && points.length > 1) {
        path = points.reduce((acc, point, index) => {
          if (index === 0) return `M ${point.x} ${point.y}`;
          const prev = points[index - 1];
          const cp1x = prev.x + (point.x - prev.x) / 3;
          const cp2x = prev.x + 2 * (point.x - prev.x) / 3;
          return `${acc} C ${cp1x} ${prev.y}, ${cp2x} ${point.y}, ${point.x} ${point.y}`;
        }, '');
      } else {
        path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
      }

      return {
        ...s,
        color,
        colors,
        points,
        path,
        isVisible,
      };
    });

    return {
      processedSeries: processed,
      yMin: paddedMin,
      yMax: paddedMax,
      yTicks: yTickValues,
      xLabels: xAxisLabels,
    };
  }, [series, activeSeries, chartWidth, chartHeight, padding, curved]);

  const toggleSeries = (id: string) => {
    setActiveSeries(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const glowId = `line-glow-${Math.random().toString(36).slice(2, 9)}`;
  const formatYAxis = yAxisFormat || ((v: number) => v.toFixed(0));

  // X-axis tick indices
  const xTickIndices = xLabels.length <= 6
    ? xLabels.map((_, i) => i)
    : [0, Math.floor(xLabels.length / 4), Math.floor(xLabels.length / 2), Math.floor((3 * xLabels.length) / 4), xLabels.length - 1];

  const aspectRatio = width / height;

  return (
    <div
      className={`nx-line-chart ${className}`}
      style={{ width: '100%', ...style }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: `${aspectRatio}` }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            <filter id={glowId}>
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Grid */}
          {showGrid && (
            <g className="nx-line-chart__grid">
              {yTicks.map((tick, i) => {
                const y = padding.top + chartHeight - ((tick - yMin) / (yMax - yMin)) * chartHeight;
                return (
                  <line
                    key={`h-${i}`}
                    x1={padding.left}
                    y1={y}
                    x2={padding.left + chartWidth}
                    y2={y}
                    stroke="rgba(0, 212, 255, 0.08)"
                    strokeWidth={1}
                  />
                );
              })}
              {xTickIndices.map((tickIndex) => {
                const x = padding.left + (tickIndex / (xLabels.length - 1)) * chartWidth;
                return (
                  <line
                    key={`v-${tickIndex}`}
                    x1={x}
                    y1={padding.top}
                    x2={x}
                    y2={padding.top + chartHeight}
                    stroke="rgba(0, 212, 255, 0.08)"
                    strokeWidth={1}
                  />
                );
              })}
            </g>
          )}

          {/* Y-axis */}
          {showYAxis && (
            <g className="nx-line-chart__y-axis">
              <line
                x1={padding.left}
                y1={padding.top}
                x2={padding.left}
                y2={padding.top + chartHeight}
                stroke="rgba(0, 212, 255, 0.2)"
                strokeWidth={1}
              />
              {yTicks.map((tick, i) => {
                const y = padding.top + chartHeight - ((tick - yMin) / (yMax - yMin)) * chartHeight;
                return (
                  <text
                    key={i}
                    x={padding.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fill="var(--nx-text-ghost)"
                    fontSize="10"
                    fontFamily="var(--nx-font-mono)"
                  >
                    {formatYAxis(tick)}
                  </text>
                );
              })}
            </g>
          )}

          {/* X-axis */}
          {showXAxis && (
            <g className="nx-line-chart__x-axis">
              <line
                x1={padding.left}
                y1={padding.top + chartHeight}
                x2={padding.left + chartWidth}
                y2={padding.top + chartHeight}
                stroke="rgba(0, 212, 255, 0.2)"
                strokeWidth={1}
              />
              {xTickIndices.map((tickIndex) => {
                const x = padding.left + (tickIndex / (xLabels.length - 1)) * chartWidth;
                return (
                  <text
                    key={tickIndex}
                    x={x}
                    y={padding.top + chartHeight + 16}
                    textAnchor="middle"
                    fill="var(--nx-text-ghost)"
                    fontSize="10"
                    fontFamily="var(--nx-font-mono)"
                  >
                    {xLabels[tickIndex]}
                  </text>
                );
              })}
            </g>
          )}

          {/* Lines */}
          {processedSeries.map((s, seriesIndex) => {
            if (!s.isVisible) return null;

            return (
              <g key={s.id}>
                {/* Glow line */}
                <path
                  d={s.path}
                  fill="none"
                  stroke={s.colors.base}
                  strokeWidth={6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.2}
                  filter={`url(#${glowId})`}
                  style={{
                    strokeDasharray: animated ? '2000' : 'none',
                    strokeDashoffset: animated ? '2000' : '0',
                    animation: animated ? `nx-border-trace 2s ease-out ${seriesIndex * 0.2}s forwards` : 'none',
                  }}
                />
                {/* Main line */}
                <path
                  d={s.path}
                  fill="none"
                  stroke={s.colors.base}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={s.dashed ? '8 4' : 'none'}
                  filter={`url(#${glowId})`}
                  style={{
                    strokeDasharray: animated && !s.dashed ? '2000' : s.dashed ? '8 4' : 'none',
                    strokeDashoffset: animated && !s.dashed ? '2000' : '0',
                    animation: animated && !s.dashed ? `nx-border-trace 2s ease-out ${seriesIndex * 0.2}s forwards` : 'none',
                  }}
                />
                {/* Data points */}
                {showDots && s.points.map((point, pointIndex) => {
                  const isHovered = hoveredPoint?.seriesIndex === seriesIndex && hoveredPoint?.pointIndex === pointIndex;
                  return (
                    <g key={pointIndex}>
                      {isHovered && (
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r={8}
                          fill="none"
                          stroke={s.colors.base}
                          strokeWidth={1}
                          opacity={0.4}
                        />
                      )}
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r={isHovered ? 5 : 3}
                        fill={s.colors.base}
                        filter={`url(#${glowId})`}
                        style={{ cursor: 'pointer', transition: 'r 0.15s ease-out' }}
                        onMouseEnter={() => setHoveredPoint({ seriesIndex, pointIndex })}
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* Hover vertical line */}
          {showTooltip && hoveredPoint && (
            <line
              x1={processedSeries[hoveredPoint.seriesIndex]?.points[hoveredPoint.pointIndex]?.x}
              y1={padding.top}
              x2={processedSeries[hoveredPoint.seriesIndex]?.points[hoveredPoint.pointIndex]?.x}
              y2={padding.top + chartHeight}
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth={1}
              strokeDasharray="4 2"
            />
          )}
        </svg>

        {/* Tooltip */}
        {showTooltip && hoveredPoint && processedSeries[hoveredPoint.seriesIndex]?.isVisible && (
          <div
            style={{
              position: 'absolute',
              left: processedSeries[hoveredPoint.seriesIndex].points[hoveredPoint.pointIndex].x,
              top: processedSeries[hoveredPoint.seriesIndex].points[hoveredPoint.pointIndex].y - 60,
              transform: 'translateX(-50%)',
              background: 'var(--nx-glass-dark)',
              border: `1px solid ${processedSeries[hoveredPoint.seriesIndex].colors.base}`,
              borderRadius: '4px',
              padding: '8px 12px',
              boxShadow: `0 0 16px ${processedSeries[hoveredPoint.seriesIndex].colors.glow}`,
              pointerEvents: 'none',
              zIndex: 10,
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ fontFamily: 'var(--nx-font-interface)', fontSize: '11px', color: 'var(--nx-text-tertiary)', marginBottom: '2px' }}>
              {processedSeries[hoveredPoint.seriesIndex].label}
            </div>
            <div style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '14px', fontWeight: 600, color: processedSeries[hoveredPoint.seriesIndex].colors.base }}>
              {processedSeries[hoveredPoint.seriesIndex].points[hoveredPoint.pointIndex].data.value}
            </div>
            {processedSeries[hoveredPoint.seriesIndex].points[hoveredPoint.pointIndex].data.label && (
              <div style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '10px', color: 'var(--nx-text-ghost)', marginTop: '2px' }}>
                {processedSeries[hoveredPoint.seriesIndex].points[hoveredPoint.pointIndex].data.label}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap', paddingLeft: padding.left }}>
          {processedSeries.map((s) => (
            <div
              key={s.id}
              onClick={() => toggleSeries(s.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                opacity: s.isVisible ? 1 : 0.4,
                transition: 'opacity 0.15s ease-out',
              }}
            >
              <div
                style={{
                  width: '16px',
                  height: '3px',
                  background: s.colors.base,
                  borderRadius: '2px',
                  boxShadow: s.isVisible ? `0 0 6px ${s.colors.glow}` : 'none',
                }}
              />
              <span style={{ fontFamily: 'var(--nx-font-interface)', fontSize: '11px', color: 'var(--nx-text-secondary)' }}>
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LineChart;
